// The WGSL twin of the instanced segment batch (`../../gfx/segments.ts`).
//
// The picture is the GLSL pair's, line for line. Three things the language
// forces: `select` takes its FALSE arm first, `mod` is a reserved word and
// its GLSL meaning comes from `glMod` (`./common.ts`), and both stages live
// in one module over one varyings struct.
//
// `fwidth` stays. The `shaders/` directory bans every screen-space
// derivative, because each field there has an analytic gradient and a
// derivative glitches on the crease the border line sits on. A capsule edge
// has no such crease: the call is at the top level of `fs`, where the
// control flow is uniform, and it is the antialiasing of every line the map
// draws.

import type { AttribSpec } from '../../gfx/types';
import type { UniformSpec } from '../prelude';
import { cornerDecodeWgsl } from '../../gl/corner';
import { wgslModule } from './module';
import { GLMOD_WGSL } from './common';

/** The varyings the two stages of the segment batch share. */
const SEGMENT_VARYINGS_WGSL = `struct VsOut {
  @builtin(position) pos: vec4f,
  @location(0) vLocal: vec2f,
  @location(1) vLen: f32,
  @location(2) vHalf: f32,
  @location(3) vRound: f32,
  @location(4) vDash: vec2f,
  @location(5) vColor: vec4f,
}
`;

const SEGMENT_VERTEX_WGSL = `@vertex
fn vs(in: VsIn) -> VsOut {
  var out: VsOut;
${cornerDecodeWgsl()}
  let flags = i32(in.aFlags + 0.5);
  let k = select(1.0, 1.0 / U.uZoom, (flags & 1) != 0);   // F_SCREEN
  let px = 1.0 / max(U.uPxPerWorld, 1e-6);                // one device px in world px
  let wWorld = in.aWidth * k;
  // half is a reserved word in GLSL ES, so the name stays halfW.
  let halfW = max(wWorld, px) * 0.5;
  let rounded = (flags & 4) != 0;                         // F_ROUND
  let d = in.aP1 - in.aP0;
  let len = length(d);
  let t = select(vec2f(1.0, 0.0), d / max(len, 1e-6), len > 1e-6);
  let n = vec2f(-t.y, t.x);
  let capExt = select(0.0, halfW, rounded) + px;
  let along = mix(-capExt, len + capExt, corner.x);
  let across = mix(-(halfW + px), halfW + px, corner.y);
  let world = in.aP0 + t * along + n * across;
  out.pos = vec4f((U.uViewProj * vec3f(world, 1.0)).xy, 0.0, 1.0);
  out.vLocal = vec2f(along, across);
  out.vLen = len;
  out.vHalf = halfW;
  out.vRound = select(0.0, 1.0, rounded);
  out.vDash = in.aDash * k;
  let fade = min(1.0, wWorld * U.uPxPerWorld);            // the hairline rule
  let a = select(in.aColor.a, 0.0, (flags & 2) != 0);     // F_ADD
  out.vColor = vec4f(in.aColor.rgb * in.aColor.a, a) * fade;
  return out;
}
`;

const SEGMENT_FRAGMENT_WGSL = `@fragment
fn fs(in: VsOut) -> @location(0) vec4f {
  // The distance along the line, SIGNED: negative inside the span, positive
  // past an end. The box branch must keep the sign. A value clamped at zero
  // makes d exactly zero over the whole interior, smoothstep(-w, w, 0.0)
  // returns 0.5, and a butt-capped line then draws at HALF its alpha and
  // steps from 0.5 to 0 at its ends. With the sign, the interior falls below
  // zero and reaches full coverage, and each end ramps like the two sides.
  // The round branch keeps the clamp, because length() cannot read a
  // negative x-distance: inside the span the cap distance is the across
  // distance alone.
  let dxr = max(-in.vLocal.x, in.vLocal.x - in.vLen);
  let d = select(
    max(abs(in.vLocal.y) - in.vHalf, dxr),
    length(vec2f(max(dxr, 0.0), in.vLocal.y)) - in.vHalf,
    in.vRound > 0.5);
  let w = max(fwidth(d), 1e-6);
  var cov = 1.0 - smoothstep(-w, w, d);
  if (in.vDash.x > 0.0) {
    let period = in.vDash.x + in.vDash.y;
    cov *= step(glMod(in.vLocal.x, period), in.vDash.x);
  }
  return in.vColor * cov * U.uAlpha;
}
`;

/** The whole WGSL module of the segment batch: the header and both stages. */
export function segmentModuleWgsl(
  specs: readonly UniformSpec[], attribs: readonly AttribSpec[],
): string {
  return wgslModule({
    uniforms: specs, attribs, varyings: SEGMENT_VARYINGS_WGSL,
    vertex: SEGMENT_VERTEX_WGSL, body: GLMOD_WGSL + SEGMENT_FRAGMENT_WGSL,
  });
}
